import { formatInTimeZone } from "date-fns-tz";
import { Avatar, Typography } from "@/components/atoms";
import { driverDelayCaption, pickupReconcilesAsSum } from "@/helpers/bookingHelpers";
import { ClientBooking } from "@/redux/apis/clientBooking/types";
import { getAssetUrl } from "@/utils";
import { BookingAction } from "./components/BookingAction";
import { BookingStatusBadge } from "./statusGroups";
import { BookingHistoryTableData } from "./types";

/**
 * The provenance caption under the pickup time — "incl. 20 min driver delay" (design node
 * `11784:130783`, stacked as the third line under date and time).
 *
 * Carries the operator table's falsehood guard: when the row holds the arrival its pickup was derived
 * from and that arrival + delay no longer equals the stored pickup, something moved the pickup after it
 * was derived, so "incl. N min" may itself be false and the claim is dropped rather than shown.
 */
const renderPickupDelayCaption = (booking: ClientBooking) => {
  const contradicted =
    !!booking.derivedArrival &&
    !pickupReconcilesAsSum({
      arrivalIso: booking.derivedArrival,
      delayMinutes: booking.pickupDelayMinutes,
      pickupIso: booking.pickupTime,
      timezone: booking.pickupTimezone,
      // The guard compares two wall clocks formatted the same way, so the format only has to be
      // consistent across both sides — "h:mm a" is what this table already renders (`formatDate`).
      timeFormat: "h:mm a",
    });

  const caption = contradicted ? null : driverDelayCaption(booking.pickupDelayMinutes);
  if (!caption) return null;

  return (
    <Typography variant="caption" className="whitespace-nowrap text-info">
      {caption}
    </Typography>
  );
};

/** Two-letter initials from a full name, for the Client avatar fallback. */
const getNameInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// Booking times display in the PICKUP's own timezone (matches the operator Bookings tables), so a
// Sydney trip reads "2:15 PM" whether the viewer is in Sydney or Manila — never the viewer's local time.
const formatDate = (dateStr: string, timezone?: string) => {
  const date = new Date(dateStr);
  if (timezone) {
    return { date: formatInTimeZone(date, timezone, "dd/MM/yyyy"), time: formatInTimeZone(date, timezone, "h:mm a") };
  }
  // Fallback only if the timezone is somehow absent — render browser-local rather than crash.
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;

  return { date: `${day}/${month}/${year}`, time: `${displayHour}:${minutes} ${ampm}` };
};

const formatCost = (booking: ClientBooking) => {
  // Pricing is hidden from invoice clients the operator has flagged (is_pricing_available=false) — the
  // legacy portal showed no price at all for them, so blank the cell rather than surface a total the
  // client isn't meant to see. Operators viewing a client's history keep seeing cost (their adapter/
  // backend sets the flag true).
  if (booking.flags?.is_pricing_available === false) return "-";
  const value = booking.total ?? booking.estimatedPayment ?? booking.vehicleTotal;
  if (value == null) return "-";
  return `$${Number(value).toFixed(2)}`;
};

/** Row-action scoping — operator client-record passes the scoped booking path + managed account id. */
export interface BookingHistoryDataOptions {
  bookingBasePath?: string;
  accountId?: string;
}

/**
 * Per-row scoping for lists whose rows belong to DIFFERENT accounts. The EA managed list (DEV-405)
 * merges every managed client's bookings with the EA's own, so one static scope can't be right for
 * all of them — each row has to route to its owning client's `book-for/:accessId` sheet.
 */
export type BookingHistoryScopeResolver = (booking: ClientBooking) => BookingHistoryDataOptions;

export const generateBookingHistoryData = (
  bookings: ClientBooking[],
  options: BookingHistoryDataOptions | BookingHistoryScopeResolver = {}
): BookingHistoryTableData[] =>
  bookings.map((booking) => {
    const scope = typeof options === "function" ? options(booking) : options;
    const { date, time } = formatDate(booking.pickupTime || booking.commencementDate, booking.pickupTimezone);

    return {
      date: (
        <div className="flex flex-col">
          <Typography>{date}</Typography>
          <Typography variant="small-regular" className="text-neutral-dark-gray">
            {time}
          </Typography>
          {renderPickupDelayCaption(booking)}
        </div>
      ),
      jobId: <Typography>{booking.displayJobId}</Typography>,
      passenger: <Typography>{booking.passengerName}</Typography>,
      // Only the managed list stamps an owning client — guarded so self / operator rows stay undefined
      // (their column isn't rendered anyway). DEV-405.
      ...(booking.managedAccount
        ? {
            managedClient: (
              <div className="flex items-center gap-2">
                <Avatar
                  size="sm"
                  src={
                    booking.managedAccount.avatar
                      ? getAssetUrl(booking.managedAccount.avatar, { height: 64, width: 64, fit: "cover" })
                      : undefined
                  }
                  alt={booking.managedAccount.name}
                  className="shrink-0 bg-neutral-mid-gray"
                >
                  {!booking.managedAccount.avatar && (
                    <Typography variant="small-regular" className="text-neutral-dark-gray">
                      {getNameInitials(booking.managedAccount.name)}
                    </Typography>
                  )}
                </Avatar>
                <Typography className="truncate">{booking.managedAccount.name}</Typography>
              </div>
            ),
          }
        : {}),
      pickupLocation: (
        <Typography className="block w-full truncate" title={booking.pickupLocation}>
          {booking.pickupLocation || "-"}
        </Typography>
      ),
      destination: (
        <Typography className="block w-full truncate" title={booking.destination}>
          {booking.destination || "-"}
        </Typography>
      ),
      cost: <Typography>{formatCost(booking)}</Typography>,
      status: <BookingStatusBadge status={booking.status} />,
      action: <BookingAction booking={booking} bookingBasePath={scope.bookingBasePath} accountId={scope.accountId} />,
    };
  });
